import { oc } from "@orpc/contract";
import { z } from "zod";

// ─── Schemas ────────────────────────────────────────────────────────────────

export const ItemCatalogItemSchema = z.object({
  // The product's explicit, caller-provided string primary key (ProductV3.key).
  key: z.string(),
  displaySku: z.string().nullable(),
  displayDescription: z.string().nullable(),
  isSellable: z.boolean(),
  isActive: z.boolean(),
  updatedAt: z.string(),
});

export const ItemCatalogFilterSchema = z.object({
  search: z.string().trim().optional(),
  // "text" (default) matches the term as a case-insensitive substring
  // (ILIKE %term%); "regex" matches it as a POSIX regular expression (~*).
  searchMode: z.enum(["text", "regex"]).default("text"),
  isSellable: z.coerce.boolean().optional(),
  isActive: z.coerce.boolean().optional(),
  sortDirection: z.enum(["asc", "desc"]).optional(),
});

export type ItemCatalogSearchMode = z.infer<
  typeof ItemCatalogFilterSchema
>["searchMode"];

// ─── Advanced filters ─────────────────────────────────────────────────────────
// Repeatable (column, operator, value) constraints, ANDed together. `column` is
// either a base ProductV3 column (see CATALOG_BASE_FILTER_COLUMNS) — filtered
// directly on im_products_v3 — or any linked raw metadata column, resolved
// against the ingest tables. String operators run case-insensitively; the two
// `is_empty` variants ignore `value`. `greater_than`/`less_than` compare
// numerically when the value is a number (metadata columns only — base text
// columns always compare lexicographically) and lexicographically otherwise.
export const CatalogFilterOperatorSchema = z.enum([
  "equals",
  "not_equals",
  "contains",
  "not_contains",
  "starts_with",
  "ends_with",
  "greater_than",
  "less_than",
  "like",
  "is_empty",
  "is_not_empty",
]);
export type CatalogFilterOperator = z.infer<typeof CatalogFilterOperatorSchema>;

// Operators that don't use `value` — the UI hides the value input for these and
// the backend ignores whatever is sent.
export const CATALOG_VALUELESS_OPERATORS: ReadonlySet<CatalogFilterOperator> =
  new Set(["is_empty", "is_not_empty"]);

export const CatalogAdvancedFilterSchema = z.object({
  column: z.string().trim().min(1),
  operator: CatalogFilterOperatorSchema,
  value: z.string().default(""),
});
export type CatalogAdvancedFilter = z.infer<typeof CatalogAdvancedFilterSchema>;

// User-facing columns that live on im_products_v3 itself (filtered directly);
// every other column name is treated as a raw metadata column resolved via the
// ingest DB. `displaySku`/`displayDescription` are text; the two flags boolean.
// The internal `key` PK is intentionally excluded — it isn't shown in the
// catalog, so there's nothing for a user to filter on.
export const CATALOG_BASE_FILTER_COLUMNS = [
  "displaySku",
  "displayDescription",
  "isSellable",
  "isActive",
] as const;
export type CatalogBaseFilterColumn =
  (typeof CATALOG_BASE_FILTER_COLUMNS)[number];

export const CATALOG_BASE_BOOLEAN_COLUMNS: ReadonlySet<string> = new Set([
  "isSellable",
  "isActive",
]);

/**
 * True when a filter column lives on im_products_v3 itself. Every other column
 * name is a raw ingest column, which is resolved by pushing the filter down to
 * the ingest tables — the distinction both ends of the API branch on (the
 * server picks the query path; the client knows whether the page is
 * cursor-addressed and whether an exact total exists).
 */
export function isCatalogBaseFilterColumn(
  column: string,
): column is CatalogBaseFilterColumn {
  return (CATALOG_BASE_FILTER_COLUMNS as readonly string[]).includes(column);
}

/**
 * True when a filter set needs the ingest scan — i.e. at least one filter
 * targets a raw ingest column.
 */
export function catalogFiltersNeedIngestScan(
  filters: readonly { column: string }[] | undefined,
): boolean {
  return (filters ?? []).some(
    (filter) => !isCatalogBaseFilterColumn(filter.column),
  );
}

/**
 * An advanced filter restricted to the base ProductV3 columns — for the
 * endpoints that can only evaluate those (the exact count; see
 * CountItemCatalogInputSchema).
 */
export const CatalogBaseAdvancedFilterSchema =
  CatalogAdvancedFilterSchema.extend({
    column: z.enum(CATALOG_BASE_FILTER_COLUMNS),
  });
export type CatalogBaseAdvancedFilter = z.infer<
  typeof CatalogBaseAdvancedFilterSchema
>;

/** The base-column subset of a filter set (see CountItemCatalogInputSchema). */
export function catalogBaseFilters(
  filters: readonly CatalogAdvancedFilter[] | undefined,
): CatalogBaseAdvancedFilter[] {
  return (filters ?? []).filter((filter): filter is CatalogBaseAdvancedFilter =>
    isCatalogBaseFilterColumn(filter.column),
  );
}

/**
 * The raw-metadata-column subset of a filter set — the half evaluated against
 * the ingest rows, and so the half that decides which of a product's data links
 * matched (see ListProductRawDataInputSchema).
 */
export function catalogMetadataFilters(
  filters: readonly CatalogAdvancedFilter[] | undefined,
): CatalogAdvancedFilter[] {
  return (filters ?? []).filter(
    (filter) => !isCatalogBaseFilterColumn(filter.column),
  );
}

/**
 * Rejects a syntactically invalid regex before it reaches Postgres, so a typo'd
 * pattern surfaces as a clean 400 (path `search`) instead of a leaked DB error.
 * Uses JS `RegExp` as the validity gate — it catches the common syntax mistakes
 * (unbalanced parens/brackets, dangling quantifiers); it does not guarantee full
 * POSIX-ERE dialect parity with Postgres's `~*`.
 */
export function refineCatalogSearch(
  val: { search?: string; searchMode: ItemCatalogSearchMode },
  ctx: z.RefinementCtx,
): void {
  if (val.searchMode === "regex" && val.search) {
    try {
      new RegExp(val.search);
    } catch {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["search"],
        message: "Invalid regular expression.",
      });
    }
  }
}

/**
 * Restrict the catalog page to an explicit set of products (ProductV3.key) —
 * the keys a `resolveItemNumbers` call handed back. Composes with the
 * base-column filters and the change-set scope, but NOT with advanced filters
 * on raw ingest columns: that path supplies its own keys from the ingest scan,
 * so a request combining the two is rejected with a 400. `listCatalog` only —
 * clients derive the count of a keyed view from the resolver's matches, so
 * `countCatalog` doesn't take keys.
 */
const catalogKeysField = z.array(z.string().min(1)).min(1).max(10000);

// Paging + filter fields shared by the catalog page endpoints (the Gently list
// and the AS/400 view). `changeSetId` restricts the catalog to the products
// staged in one metadata change set — the review detail page's Queries views.
const catalogPageFields = {
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  advancedFilters: z.array(CatalogAdvancedFilterSchema).max(20).optional(),
  changeSetId: z.string().uuid().optional(),
  /**
   * Where to resume when the request needs the ingest scan — i.e. whenever an
   * advanced filter targets a raw ingest column, and always for the AS/400
   * view. Such a page can't be addressed by number: the filters are evaluated
   * on the ingest server, one table at a time in item-number order, so "page 7"
   * could only be reached by redoing pages 1–6. `page` is ignored on that path;
   * clients follow `pageInfo.nextCursor` instead (and keep the cursors they
   * have visited to step back). Opaque — treat it as a token, not a position.
   */
  cursor: z.string().max(2048).optional(),
};

export const ListItemCatalogInputSchema = ItemCatalogFilterSchema.extend({
  ...catalogPageFields,
  keys: catalogKeysField.optional(),
  // Restrict the catalog to the products of one org-shared item list —
  // the same membership-subquery scope as `changeSetId`. A soft-deleted
  // list matches nothing.
  itemListId: z.string().uuid().optional(),
}).superRefine(refineCatalogSearch);

// ─── AS/400 view column groups ───────────────────────────────────────────────

export const As400ViewGroupSchema = z.enum([
  "ITEMMAST5",
  "ADDITIONAL_DESCRIPTION",
  "GAINS_COST",
  "ASSOCIATED_ITEMS",
  "QUANTITIES",
]);
export type As400ViewGroup = z.infer<typeof As400ViewGroupSchema>;

// ─── AS/400 export file formats ──────────────────────────────────────────────
// A second, mutually-exclusive set of column filters keyed to the AS/400
// item-master *export* file formats (ITEM, PURCHASE DATA, PRICE, ITEM ADD,
// MATRIX CLASSIFICATION). Selecting one export format shows only that format's
// columns and clears any active "AS/400 View" groups (and vice-versa).
export const As400ExportGroupSchema = z.enum([
  "ITEM",
  "PURCHASE_DATA",
  "PRICE",
  "ITEM_ADD",
  "MATRIX_CLASSIFICATION",
]);
export type As400ExportGroup = z.infer<typeof As400ExportGroupSchema>;

/**
 * Column sets for the AS/400 ITEMMAST5-style raw data. When the on-screen
 * "AS/400 View" toggle selects one or more groups, only the union of these
 * columns is kept from a linked raw-data table — in both the expanded row
 * and the CSV export, so the two stay in sync. Shared here so backend and
 * frontend don't duplicate the (large) column lists.
 */
export const AS400_VIEW_GROUP_COLUMNS: Record<
  As400ViewGroup,
  ReadonlySet<string>
> = {
  ITEMMAST5: new Set([
    "CDL",
    "ITEMNO",
    "PGSG",
    "PCSC",
    "IEDESC",
    "IFDESC",
    "PCPRC1",
    "ISLUOM",
    "IPPRQT",
    "PCPRC2",
    "PCPRC3",
    "PCPRC4",
    "PCPRC5",
    "PCPRC6",
    "PCPRC7",
    "PCPRC8",
    "PCPRC9",
    "IBCST",
    "ILCST",
    "ISUOM",
    "IALPH1",
    "IALPH2",
    "IALPH3",
    "ITARIF",
    "ISHPQT",
    "IQTCTN",
    "HAZMAT",
    "ISMQT1",
    "ISMAM1",
    "IBUYER",
    "ICODE1",
    "ICODE3",
    "ISUBCD",
    "ISUBNO",
    "ISPRD1",
    "ISUOM1",
    "IPAK1",
    "IGLO1",
    "UPC",
    "IPAK2",
    "IGLO2",
    "UPC2",
    "IPAK3",
    "IGLO3",
    "UPC3",
    "ISQTE1",
    "ISTAT",
    "ICODE4",
    "ICAT1",
    "ICAT2",
    "ICRTDT",
    "IRVDAT",
    "ISTKCD",
    "ISUPL1",
    "SUPP_NAME_CITY",
    "ISCTR1",
    "ISUFC1",
    "ISUPL2",
    "ISPRD2",
    "ISQTE2",
    "IMID2",
    "IITM2",
    "QTYSOLD01",
    "QOHSTK01",
    "AVAILABLE",
    "SQTYOO01",
    "ASSOCITEM",
    "ICAT5",
    "IOWRN1",
    "IOWRN2",
    "IOWRN3",
    "ICODE2",
    "ISNAM2",
    "IMXCLASS",
    "IOTH1E",
    "IOTH2E",
    "IOTH1F",
    "IOTH2F",
    "ISTATH",
    "IMXPRLN",
    "ISUPL3",
    "ISPRD3",
    "ISCTR3",
    "ISNAM3",
    "IMXPRCL",
    "IMXPRTG",
    "IMXPRFL",
    "ICCST",
    "ICODE9",
    "ICODE6",
    "ICODE5",
    "ICODE7",
    "ICODE8",
    "ICAT3",
    "ICAT4",
    "ISQTE3",
    "IMXSCOR",
    "IWIGHT",
    "IWUOM",
    "IMXUPDF",
    "IMXRNDF",
    "IQTEU1",
    "IQTEU2",
    "IQTEU3",
    "IQTFU1",
    "IQTFU2",
    "IQTFU3",
    "IHIGEA",
    "IWIDEA",
    "ILENEA",
    "IWHTEA",
    "IDNSEA",
    "IHIGPG",
    "IWIDPG",
    "ILENPG",
    "IWHTPG",
    "IDNSPG",
    "IHIGPT",
    "IWIDPT",
    "ILENPT",
    "IWHTPT",
    "IDNSPT",
    "SUPPLIER_NAME",
    "ISUOM3",
    "ISMQT3",
    "ISMAM3",
    "ISUFC3",
    "IMXMCCHG",
  ]),
  ADDITIONAL_DESCRIPTION: new Set([
    "ITEMNO",
    "IEDESC",
    "ISTAT",
    "ISUPL1",
    "SUNAME",
    "IDLANG",
    "IDLINE",
    "IDDESC",
  ]),
  GAINS_COST: new Set([
    "CNTR#",
    "LINE#",
    "CNTDESC",
    "STRDATE",
    "ENDDATE",
    "VENDR#",
    "VNDCNTR",
    "LOC",
    "DSTRC",
    "PRODUCT",
    "MINQTY",
    "INCQTY",
    "BPQTY1",
    "BPCST1",
    "IBCST",
    "BPQTY2",
    "BPCST2",
    "BPQTY3",
    "BPCST3",
    "DETGLA",
    "DETCSF",
    "STATUS",
  ]),
  ASSOCIATED_ITEMS: new Set([
    "ASDIV",
    "ASLOC",
    "IGROUP",
    "ASPROD",
    "IEDESC",
    "ASITEM",
    "IEDESC01",
    "QTY",
    "SOLD FLAG",
    "TYPE",
  ]),
  QUANTITIES: new Set([
    "ITEM NO",
    "DIVISION",
    "BRANCH LETTER",
    "TOTAL ON HAND",
    "ALLOCATED",
    "PRE-ALLOCATED",
    "VDR",
    "BACK ORDER",
    "AVAILABLE",
    "BOOKED",
    "ON ORDER",
    "NET AVAILABLE",
    "INTER-DEPOT",
    "IN CONSIGNMENT",
    "LOC. IN WAREHOUSE",
    "BUCKET QTY",
    "UNIT COST",
  ]),
};

// ─── AS/400 record keys: company / division / branch ─────────────────────────

/**
 * The AS/400 key columns of one ingest schema, by role. Every file spells them
 * with its own prefix (`icomp`/`imxcmp`/`ascmp`/`acomp`…) and a few carry only
 * some of the three, so the roles are declared rather than positional: AS/400
 * names the branch `loc`(ation), and `as_gscost` carries that alone while
 * `as_pcovdet` carries no branch at all.
 */
export interface As400CdbColumns {
  company?: string;
  division?: string;
  /** The location column — the "B" users read as branch. */
  branch?: string;
}

/**
 * The company/division/branch ("CDB") key columns of each AS/400 ingest schema —
 * the keys that say which record a row actually is.
 *
 * Keys are schema definition names; the physical ingest tables carry a division
 * prefix (`qe_as_item`, `qo_as_item` → `as_item`). The mapping covers every
 * ingest schema that has any of the three, and is exhaustive by design: a
 * schema with no entry here has no CDB rather than guessing at column names.
 */
export const AS400_CDB_COLUMNS_BY_SCHEMA: Record<string, As400CdbColumns> = {
  as_gscost: { branch: "loc" },
  as_invms: { company: "icomp", division: "idiv", branch: "iloc" },
  as_item: { company: "icomp", division: "idiv", branch: "iloc" },
  as_itemad: { company: "idcmpx", division: "iddivx", branch: "idloc" },
  as_miitm: { company: "acomp", division: "adiv", branch: "aloc" },
  as_pcovdet: { company: "pcodcmp", division: "pcoddiv" },
  as_prass: { company: "ascmp", division: "asdiv", branch: "asloc" },
  as_price: { company: "pcomp", division: "pdiv", branch: "ploc" },
  as_prmxcl: { company: "imxcmp", division: "imxdiv", branch: "imxloc" },
  as_prsls: { company: "scomp", division: "sdiv", branch: "sloc" },
  as_purmastd: { company: "icomp", division: "idiv", branch: "iloc" },
  as_purmasth: { company: "icomp", division: "idiv", branch: "iloc" },
  as_stock: { company: "scomp", division: "sdiv", branch: "sloc" },
};

/** A schema's key columns by role, read case-insensitively. */
function cdbColumnsOfSchema(schemaName: string): As400CdbColumns | undefined {
  return AS400_CDB_COLUMNS_BY_SCHEMA[schemaName.toLowerCase()];
}

/** The schema behind a physical ingest table, matched on the prefix boundary so
 * `qe_as_itemad` resolves to `as_itemad` and never to `as_item`. */
function cdbColumnsOfTable(tableName: string): As400CdbColumns | undefined {
  const lower = tableName.toLowerCase();
  for (const [schemaName, columns] of Object.entries(
    AS400_CDB_COLUMNS_BY_SCHEMA,
  )) {
    if (lower === schemaName || lower.endsWith(`_${schemaName}`)) {
      return columns;
    }
  }
  return undefined;
}

/** A schema's key columns in company/division/branch order (roles it has). */
function orderedCdbColumns(columns: As400CdbColumns): string[] {
  return [columns.company, columns.division, columns.branch].filter(
    (column): column is string => column !== undefined,
  );
}

/**
 * A schema definition's CDB key columns, in company/division/branch order.
 * Undefined for a schema the mapping doesn't cover — which is not the same as a
 * mapped schema whose table is missing the columns.
 */
export function as400CdbColumnsForSchema(
  schemaName: string,
): readonly string[] | undefined {
  const columns = cdbColumnsOfSchema(schemaName);
  return columns && orderedCdbColumns(columns);
}

/**
 * The CDB key columns of the schema behind a physical ingest table —
 * `qe_as_item` → `as_item`'s `icomp`/`idiv`/`iloc`. Empty for a table of an
 * unmapped schema.
 */
export function as400CdbColumnsForTable(tableName: string): readonly string[] {
  const columns = cdbColumnsOfTable(tableName);
  return columns ? orderedCdbColumns(columns) : [];
}

/**
 * The branch (location) column of the schema behind a physical ingest table —
 * `qe_as_price` → `ploc`. Undefined for a schema with no branch key of its own
 * (`as_pcovdet`) or one the mapping doesn't cover.
 */
export function as400BranchColumnForTable(
  tableName: string,
): string | undefined {
  return cdbColumnsOfTable(tableName)?.branch;
}

/**
 * One record's CDB key: its key values trimmed and concatenated in
 * company/division/branch order — `W`/`J`/`H` reads as `WJH`. A blank key
 * contributes nothing rather than leaving a gap, so all-blank is `""` (the
 * record has no key to show).
 *
 * The keys are AS/400 char and numeric fields. Anything else — a null, an
 * object an ingest quirk left on the row — has no reading as one letter of a
 * key, so it drops out rather than concatenating as `[object Object]`.
 */
export function as400CdbKey(parts: readonly unknown[]): string {
  let key = "";
  for (const part of parts) {
    if (typeof part === "string") key += part.trim();
    else if (typeof part === "number" || typeof part === "bigint") {
      key += String(part);
    }
  }
  return key;
}

/**
 * The synthetic cells an export worksheet row carries its record key under — a
 * `__`-prefixed key, so the grid treats them as read-only metadata and they stay
 * out of the worksheet's own (workbook-verbatim) columns and download.
 *
 * `AS400_CDB_COLUMN` is the whole key as one string, for the review grid's
 * leading column; `AS400_BRANCH_COLUMN` is the branch alone, which is what the
 * export splits a branch-per-file division's rows by.
 */
export const AS400_CDB_COLUMN = "__cdb__";
export const AS400_BRANCH_COLUMN = "__branch__";

/**
 * A division's AS/400 code, from its DataSource title: the company and division
 * letters of every CDB key its records carry (`WJ` of `WJH`), and the whole CDB
 * of a row with no ingest record behind it (an authored new row). Titles pick up
 * cosmetic prefixes and padding ("%RD", "rd "), so matching strips
 * non-alphanumerics and upper-cases.
 */
export function as400DivisionCode(title: string): string {
  return title.replace(/[^a-z0-9]/gi, "").toUpperCase();
}

/**
 * The branches of each AS/400 division, keyed by division code → branch codes.
 * Derived from the AS/400 ingest stock data: the location codes each division
 * actually stocks (single alphanumeric codes with at least 500 stock rows;
 * lower/uppercase variants and punctuation codes are EBCDIC-conversion noise of
 * the same locations). RM, RY, and WF carry no stock locations above noise, so
 * they have no entry and price division-wide only. Update a division's row here
 * if its branch set changes.
 *
 * These are **stocking locations** (`as_stock.sloc`), which is not the location
 * key the other file types are keyed by: RD stocks D/E/K/M/O/V while its item
 * records (`as_item.iloc`) are A/B/C/F/H/I/J and its prices (`as_price.ploc`)
 * are C/F/H, so the sets can be disjoint. Never use this list to decide which
 * locations a worksheet's records exist under — read those from the records
 * (see `As400ExportViewService.changedBranchesByDataSource`). It is a pricing
 * input, nothing more.
 */
export const AS400_BRANCHES_BY_DIVISION: Record<string, readonly string[]> = {
  // prettier-ignore
  QE: ["2", "3", "5", "6", "8", "9", "D", "E", "F", "H", "J", "L", "M", "N", "O", "P", "R", "S", "T", "V", "W", "X", "Z"],
  // prettier-ignore
  QO: ["A", "B", "C", "D", "E", "G", "I", "J", "K", "L", "O", "Q", "R", "S", "V", "Y", "Z"],
  // prettier-ignore
  RA: ["8", "A", "B", "C", "G", "H", "J", "K", "L", "M", "N", "S", "T", "W"],
  RD: ["D", "E", "K", "M", "O", "V"],
  RH: ["E", "N", "S"],
  RL: ["B", "C", "E", "F", "I", "J"],
  UU: ["B", "O"],
  // prettier-ignore
  WB: ["A", "C", "D", "F", "G", "H", "I", "J", "L", "N", "P", "Q", "R"],
  // prettier-ignore
  WG: ["0", "1", "4", "6", "8", "A", "B", "D", "E", "F", "G", "H", "I", "J", "K", "M", "N", "O", "P", "Q", "R", "T", "U", "V", "W", "X", "Y", "Z"],
  // prettier-ignore
  WJ: ["2", "6", "7", "8", "A", "B", "D", "E", "F", "H", "J", "K", "L", "M", "N", "O", "P", "T", "V", "Y"],
  // prettier-ignore
  WR: ["1", "2", "C", "D", "E", "G", "H", "I", "J", "K", "M", "N", "O", "P", "W", "X", "Y"],
  // prettier-ignore
  WT: ["1", "3", "A", "C", "D", "E", "F", "G", "H", "I", "K", "M", "N", "S", "T", "U", "V", "W", "X", "Y", "Z"],
};

/**
 * The branch letters for a division, given its DataSource title (normalized to
 * its AS/400 code by {@link as400DivisionCode}). Empty for a division that
 * stocks no branches of its own.
 */
export function as400BranchesForDivision(title: string): readonly string[] {
  return AS400_BRANCHES_BY_DIVISION[as400DivisionCode(title)] ?? [];
}

/**
 * Divisions whose worksheet export is split one file per branch instead of one
 * file for the division: RD's AS/400 upload is run per branch, so a single
 * division-wide file cannot be loaded. Every other division exports as one file
 * however many branches it stocks.
 *
 * Codes, as {@link as400DivisionCode} normalizes a DataSource title.
 */
export const AS400_BRANCH_SPLIT_DIVISIONS: ReadonlySet<string> = new Set([
  "RD",
]);

/**
 * Whether this division's worksheet export is split one file per branch.
 *
 * Only says *that* it splits, never which branches: those are the locations the
 * exported records are actually keyed by, so they are read from the records
 * themselves. {@link AS400_BRANCHES_BY_DIVISION} is the stocking-location list
 * and is a different set — using it here filed RD's every changed row under a
 * branch none of its item records belong to, and the export came out empty.
 */
export function as400DivisionExportsPerBranch(title: string): boolean {
  return AS400_BRANCH_SPLIT_DIVISIONS.has(as400DivisionCode(title));
}

/**
 * Column layout for the AS/400 item-master *export* worksheets. Each export
 * group reproduces one tab of the Rexel "Product Upload" workbook: the
 * on-screen Export table and the CSV/XLSX download show that tab's columns
 * verbatim — same headings, same order, none dropped.
 *
 *   ITEM                  → "ITEM WORKSHEET"            (source: item.json)
 *   PURCHASE_DATA         → "PURCHASE DATA WORK SHEET"  (purmasth.json + purmastd.json)
 *   PRICE                 → "PRICE WORK SHEET"          (price.json)
 *   ITEM_ADD              → "ITEM ADD WORKSHEET"        (itemad.json)
 *   MATRIX_CLASSIFICATION → "MATRIX CLASSIFICATION"     (prmxcl.json)
 *
 * `header` is the worksheet heading, kept verbatim (may contain line breaks or
 * be empty). `code` is the AS/400 raw-table field the value is sourced from, or
 * `null` when the column has no counterpart we can fill yet — export-only
 * "change flag"/"reset"/"update Y/N" pseudo-columns and a few item fields with
 * no ingest field (HazMat, shelf-life, serialization); those render blank.
 * `lowConfidence` flags a probable-but-unconfirmed field match.
 *
 * `source` declares which of the group's ingest file types owns `code`, as the
 * table-name suffix from {@link AS400_EXPORT_GROUP_SCHEMA_SUFFIXES}. It is what
 * maps an edited export cell back to the ingested raw column it must update —
 * required on every mapped column of a multi-file-type group (Purchase Data),
 * where "the first table that happens to have a column of that name" is not a
 * safe rule. Single-file-type groups omit it; their owner is unambiguous.
 */
export interface As400ExportColumn {
  /** Worksheet heading, verbatim (may contain line breaks or be empty). */
  header: string;
  /** AS/400 raw-table field the value is sourced from; null = unmapped (blank). */
  code: string | null;
  /**
   * Stable identity for an export-only column (`code: null`): the worksheet's
   * change-flag / reset / update-Y/N pseudo-columns and the few item fields
   * with no ingest counterpart. Giving them a key is what makes them editable
   * in a review — their staged values are stored under it, deliberately apart
   * from the ingest field codes so a future ingest sync can never mistake a
   * worksheet-only value for an ingest field edit. A `code: null` column with
   * no key (the Item Add spacer) stays read-only.
   *
   * Keys are unique within their group, across codes and keys alike.
   */
  key?: string;
  /**
   * Owning ingest file type: the table-name suffix `code` lives under. Set on
   * every mapped column of a group with more than one file type.
   */
  source?: string;
  /** True when the field match is a best guess, not confirmed. */
  lowConfidence?: boolean;
  /**
   * The most characters the AS/400 accepts in this column's cell, when the
   * workbook states one. Enforced on the review grid so an upload file can't
   * be authored with a value the AS/400 will reject or silently truncate.
   *
   * A cell holds *text*, so this is the character width of the value as it is
   * typed — not the field's storage size. Sources, strongest first:
   *
   * - **The AS/400 field listing for the file.** Its `Length` is the field's
   *   byte footprint in the record, which is the character width only for type
   *   `A`. Packed (`P`) and binary (`B`) fields state their digits separately
   *   (`IBCST` is `Length 5` but 9 digits — packed decimal stores two digits a
   *   byte plus a sign nibble). Take `Length` for `A`, digit count for `P`/`B`.
   *   The whole ITEM group is encoded from its listing.
   * - The column's own workbook note — "Max length: 10A", "Character 5".
   * - The header's own mask — `SHP'G MULT 99999` (5), `LEAD TIME 999` (3).
   *   A mask is worksheet-level and can span more than one ingest field, so a
   *   listing overrules it: `REXEL PROD CLASS 9999999` looks like 7, but
   *   `IBARCD` holds 5 and the remaining 2 live in the `FILL01` field beside
   *   it.
   * - The ingest field's `NUMERIC(precision, scale)` in
   *   `backend/test-data/rexel/as400_schema_jsons`, whose `precision` is the
   *   same digit count the listings give (the fixtures derive from them).
   * - A Y/N (or Y/N/D) pseudo-column: one character.
   * - Carried across files on the owner's say-so, where no listing covers the
   *   file yet: the product number is 20 in all five groups (`ITEMNO`,
   *   `IDITEM`, `IMXITEM` — it is the same item-master key everywhere), and
   *   `IDDESC` is 40, matching the item file's descriptions.
   *
   * **The decimal point counts only where it is typed.** A numeric column is
   * its digit count plus one for the point — except where the header marks the
   * decimal as implied (`2 IMPL DEC`, `5 IML DEC`, `2 IMP DEC`), which tells
   * the person filling the sheet to enter `123456` for `1234.56`. Those take
   * the digits alone. Note also that a leading `-` spends one of these
   * characters, the masks having no sign position — the documented "enter -1
   * to clear" idiom still fits.
   *
   * **Undefined means unknown, not unlimited.** Every AS/400 field has a real
   * width, but only the ITEM listing is on hand: the ingest fixtures carry no
   * length for alpha fields, and the ingest tables type every one of them
   * `VARCHAR(255)` regardless (see `SQL_SCHEMA_STRING_MAX_LENGTH`). ITEM,
   * PRICE and MATRIX_CLASSIFICATION are complete; the 43 columns left
   * undefined are 41 of PURCHASE_DATA's (supplier codes, UPCs, names, warn
   * codes, remarks) and ITEM_ADD's `IDLANG`/`IDLINE`. They are waiting on the
   * field listings for purmasth, purmastd and itemad. A guessed limit would
   * silently corrupt an upload file, so they stay unconstrained until then.
   */
  maxLength?: number;
  /** Worksheet cell note, shown as a header tooltip. Verbatim (may be multi-line). */
  comment?: string;
}

/**
 * The identity an export column's cells are read and edited by: its ingest
 * field code when mapped, its export-only key otherwise, null when neither
 * (a spacer — not editable, always blank).
 */
export function as400ColumnEditKey(column: As400ExportColumn): string | null {
  return column.code ?? column.key ?? null;
}

/**
 * The export-only column keys of a group — the editable columns whose values
 * live only on the worksheet, never in an ingest table.
 */
export function as400ExportOnlyColumnKeys(
  group: As400ExportGroup,
): ReadonlySet<string> {
  const keys = new Set<string>();
  for (const column of AS400_EXPORT_WORKSHEET_COLUMNS[group]) {
    if (!column.code && column.key) keys.add(column.key);
  }
  return keys;
}

/**
 * Ingest table-name suffixes identifying each export group's AS/400 file
 * type(s): a data-source link feeds a group when its `foreign_table_name` ends
 * in one of these (e.g. `qe_as_price` → `_as_price`). A group with more than
 * one suffix has its file types joined per division on the group's join key —
 * the first mapped worksheet column.
 *
 * Lives here, next to the column mapping, so "which raw table owns this
 * worksheet column" ({@link As400ExportColumn.source}) and "which raw tables
 * feed this worksheet" are declared in one place.
 */
export const AS400_EXPORT_GROUP_SCHEMA_SUFFIXES: Record<
  As400ExportGroup,
  readonly string[]
> = {
  ITEM: ["_as_item"],
  PURCHASE_DATA: ["_as_purmasth", "_as_purmastd"],
  PRICE: ["_as_price"],
  ITEM_ADD: ["_as_itemad"],
  MATRIX_CLASSIFICATION: ["_as_prmxcl"],
};

// The two Purchase Data file types, named once for the column annotations.
const PURMASTH = "_as_purmasth";
const PURMASTD = "_as_purmastd";

export const AS400_EXPORT_WORKSHEET_COLUMNS: Record<
  As400ExportGroup,
  readonly As400ExportColumn[]
> = {
  ITEM: [
    {
      header: "PRODUCT NUMBER",
      code: "ITEMNO",
      maxLength: 20,
      comment: "Must be all upper case and cannot contain space.",
    },
    { header: "PG", code: "IGROUP", maxLength: 2 },
    { header: "SG", code: "ISGRUP", maxLength: 2 },
    { header: "PC", code: "ICLASS", maxLength: 2 },
    { header: "SC", code: "ISCLAS", maxLength: 2 },
    { header: "ENGLISH DESCRIPTION", code: "IEDESC", maxLength: 40 },
    { header: "FRENCH DESCRIPTION", code: "IFDESC", maxLength: 40 },
    { header: "KEYWORD 1 (10 CHARS)", code: "IALPH1", maxLength: 10 },
    {
      header: "KEYWORD 2 (10 CHARS)",
      code: "IALPH2",
      maxLength: 10,
      comment: "Max length: 10A\nEnter '////////// for blank value",
    },
    {
      header: "KEYWORD 3 (10 CHARS)",
      code: "IALPH3",
      maxLength: 10,
      comment: "Max length: 10A\nEnter '////////// for blank value",
    },
    { header: "TARIFF", code: "ITARIF", maxLength: 2 },
    { header: "SHP'G MULT 99999", code: "ISHPQT", maxLength: 5 },
    { header: "CTN QTY 9999", code: "IQTCTN", maxLength: 4 },
    { header: "BUYER", code: "IBUYER", maxLength: 1 },
    { header: "WEIGHT 9999.999", code: "IWIGHT", maxLength: 8 },
    { header: "LB or KG", code: "IWUOM", maxLength: 2 },
    { header: "STOCK UOM", code: "ISUOM", maxLength: 2 },
    { header: "PRICING FACTOR 9999.99999", code: "IPPRQT", maxLength: 10 },
    { header: "SELL UOM", code: "ISLUOM", lowConfidence: true, maxLength: 2 },
    { header: "BUYER'S COST 99999.9999", code: "IBCST", maxLength: 10 },
    { header: " LANDED COST 99999.9999", code: "ILCST", maxLength: 10 },
    { header: "USER DEF 1", code: "ICODE1", maxLength: 1 },
    { header: "USER DEF 2", code: "ICODE2", maxLength: 1 },
    {
      header: "USER DEFINED FIELD 3 (10 CHARS)",
      code: "ICODE3",
      maxLength: 10,
      comment: "Enter '/' to blank out field in an update mode",
    },
    { header: "LEAD TIME 999", code: "IPLEAD", maxLength: 3 },
    { header: "SUB ", code: "ISUBCD", maxLength: 1 },
    {
      header: "SUBSTITUTE PROD CODE (20 CHARS)",
      code: "ISUBNO",
      maxLength: 20,
      comment:
        "Allow Sub Item to be blanked out if Sub Code = '0' and uploaded Sub Item has all '/'s' in it",
    },
    { header: "PROD REC STATUS (1 CHAR)", code: "ISTAT", maxLength: 1 },
    {
      header: "UPDATE PROD SEQ # (Y/N)",
      code: null,
      key: "UPDATE_IPRDSQ",
      maxLength: 1,
    },
    { header: "PROD SEQ # 9999999", code: "IPRDSQ", maxLength: 7 },
    {
      header: "REXEL PROD CLASS 9999999",
      code: "IBARCD",
      lowConfidence: true,
      maxLength: 5,
    },
    {
      header: "CHANGE STATUS FLAG (Y/N)",
      code: null,
      key: "CHANGE_STATUS_FLAG",
      maxLength: 1,
    },
    { header: "Qty Per Case1 Eng", code: "IQTEU1", maxLength: 5 },
    { header: "Qty Per Case2 Eng", code: "IQTEU2", maxLength: 5 },
    { header: "Qty Per Case3 Eng", code: "IQTEU3", maxLength: 6 },
    { header: "Qty Per Case1 Frn", code: "IQTFU1", maxLength: 5 },
    { header: "Qty Per Case2 Frn", code: "IQTFU2", maxLength: 5 },
    { header: "Qty Per Case3 Frn", code: "IQTFU3", maxLength: 6 },
    {
      header: "USER DEF 4",
      code: "ICODE4",
      maxLength: 1,
      comment:
        "User defined field 4.\nMax length: 1AN\nEnter '/' for blank value.\nValidation based on user defined constant 'DTMH'.",
    },
    {
      header: "USER DEF 5",
      code: "ICODE5",
      maxLength: 1,
      comment:
        "User defined field 5.\nMax length: 1AN\nEnter '/' for blank value.\nValidation based on user defined constant 'DTMH'.",
    },
    {
      header: "USER DEF 6",
      code: "ICODE6",
      maxLength: 1,
      comment:
        "User defined field 6.\nMax length: 1AN\nEnter '/' for blank value.\nValidation based on user defined constant 'DTMH'.",
    },
    {
      header: "USER DEF 7",
      code: "ICODE7",
      maxLength: 1,
      comment:
        "User defined field 7.\nMax length: 1AN\nEnter '/' for blank value.\nValidation based on user defined constant 'DTMH'.",
    },
    {
      header: "USER DEF 8",
      code: "ICODE8",
      maxLength: 1,
      comment:
        "User defined field 8.\nMax length: 1AN\nEnter '/' for blank value.\nValidation based on user defined constant 'DTMH'.",
    },
    {
      header: "USER DEF 9",
      code: "ICODE9",
      maxLength: 1,
      comment:
        "User defined field 9.\nMax length: 1AN\nEnter '/' for blank value.\nValidation based on user defined constant 'DTMH'.",
    },
    {
      header: "Item Cateory1",
      code: "ICAT1",
      maxLength: 5,
      comment:
        "Item Category 1.\nMax length: 5AN\nRight justified\nEnter '/////' for blank value.",
    },
    {
      header: "Item Cateory2",
      code: "ICAT2",
      maxLength: 5,
      comment:
        "Item Category 2.\nMax length: 5AN\nRight justified\nEnter '/////' for blank value.",
    },
    {
      header: "Item Cateory3",
      code: "ICAT3",
      maxLength: 5,
      comment:
        "Item Category 3.\nMax length: 5AN\nRight justified\nEnter 'Y' or 'N' value in 2nd position.\nUsed to flag the product as an Assembly Item",
    },
    {
      header: "Item Cateory4",
      code: "ICAT4",
      maxLength: 5,
      comment:
        "Item Category 4.\nMax length: 5AN\nRight justified\nEnter '/////' for blank value.",
    },
    {
      header: "Item Cateory5",
      code: "ICAT5",
      maxLength: 5,
      comment:
        "Item Category 5.\nMax length: 5AN\nRight justified\nEnter '/////' for blank value.",
    },
    {
      header: "Change Flag for Qty Per Case1 Eng",
      code: null,
      key: "CHG_IQTEU1",
      maxLength: 1,
    },
    {
      header: "Change Flag for Qty Per Case2 Eng",
      code: null,
      key: "CHG_IQTEU2",
      maxLength: 1,
    },
    {
      header: "Change Flag for Qty Per Case3 Eng",
      code: null,
      key: "CHG_IQTEU3",
      maxLength: 1,
    },
    {
      header: "Change Flag for Qty Per Case1 Frn",
      code: null,
      key: "CHG_IQTFU1",
      maxLength: 1,
    },
    {
      header: "Change Flag for Qty Per Case2 Frn",
      code: null,
      key: "CHG_IQTFU2",
      maxLength: 1,
    },
    {
      header: "Change Flag for Qty Per Case3 Frn",
      code: null,
      key: "CHG_IQTFU3",
      maxLength: 1,
    },
    {
      header: "Prd Type",
      code: "ITYPE",
      maxLength: 1,
      comment:
        "Valid Product Type =\n'T' (POTF Template)\n'W'(AOTF Template)\n'F' and ' '\nMax Length 1A",
    },
    {
      header: "HazMat",
      code: null,
      key: "HAZMAT_FLAG",
      maxLength: 1,
      comment:
        "Hazardous Material ADD mode -must be Y or N. CHANGE mode must be Y or N or Blank (do not change)",
    },
    {
      header: "SHELF LIFE CONTROLLED FLAG",
      code: null,
      key: "SHELF_LIFE_FLAG",
      maxLength: 1,
      comment: "Shelf Life Controlled Flag\nMax Length: 1AN\nValid Values: Y/N",
    },
    {
      header: "CORE COST 99999.9999",
      code: "ICCST",
      maxLength: 10,
      comment:
        "Note:\n-Leave this field blank or zero if no change is make to this field.\n-Enter -1 to clear the existing value on the item master file.",
    },
    {
      header: "Product Serialization Flag (Y/N)",
      code: null,
      key: "SERIALIZATION_FLAG",
      maxLength: 1,
    },
  ],
  // Purchase Data spans two file types: supplier data (purmasth) and dimension
  // data (purmastd). Every mapped column declares which one owns it — the join
  // key (ITEMNO) exists in both, and an edit to it is fanned out to both rows.
  PURCHASE_DATA: [
    {
      header: "\nPRODUCT CODE",
      code: "ITEMNO",
      source: PURMASTH,
      maxLength: 20,
    },
    { header: "SUPL#\n1", code: "ISUPL1", source: PURMASTH },
    { header: "CNTRY", code: "ISCTR1", source: PURMASTH },
    {
      header: "SUPPLIER 1\nPRODUCT #",
      code: "ISPRD1",
      source: PURMASTH,
      maxLength: 25,
      comment:
        ".Supplier 1 Prouduct #\n.Max length: 25 (AN)\n.Enter '/////////////////////////' (total 25 '/')             for blank value.\n.Leave this field blank if no uploaded data",
    },
    { header: "UOM", code: "ISUOM1", source: PURMASTH },
    {
      header: "UOM\nFACTOR\n5 IML DEC",
      code: "ISUFC1",
      source: PURMASTH,
      maxLength: 9,
    },
    { header: "MIN\nQTY", code: "ISMQT1", source: PURMASTH },
    {
      header: "MIN\nAMT $\n2 IMP DEC",
      code: "ISMAM1",
      source: PURMASTH,
      maxLength: 9,
    },
    { header: "UPC PACK 1ST Char", code: "IPAK1", source: PURMASTH },
    { header: "UPC MFTR ID 2ND Char", code: "IMID1", source: PURMASTH },
    {
      header: "SUPLR\nUPC",
      code: "IGLO1",
      source: PURMASTH,
      lowConfidence: true,
    },
    { header: "ITEM\nUPC", code: "IITM1", source: PURMASTH },
    { header: "SD", code: "ISHD1", source: PURMASTH },
    { header: "SUPL#\n2", code: "ISUPL2", source: PURMASTH },
    { header: "CNTRY", code: "ISCTR2", source: PURMASTH },
    {
      header: "SUPPLIER 2\nPRODUCT #",
      code: "ISPRD2",
      source: PURMASTH,
      maxLength: 25,
      comment:
        ".Supplier 2 Prouduct #\n.Max length: 25 (AN)\n.Enter '/////////////////////////' (total 25 '/')             for blank value.\n.Leave this field blank if no uploaded data.",
    },
    { header: "UOM", code: "ISUOM2", source: PURMASTH },
    {
      header: "UOM\nFACTOR\n5 IML DEC",
      code: "ISUFC2",
      source: PURMASTH,
      maxLength: 9,
    },
    { header: "MIN\nQTY", code: "ISMQT2", source: PURMASTH },
    {
      header: "MIN\nAMT $\n2 IMPL DEC",
      code: "ISMAM2",
      source: PURMASTH,
      maxLength: 9,
    },
    { header: "UPC PACK 1ST Char", code: "IPAK2", source: PURMASTH },
    { header: "UPC MFTR ID 2ND Char", code: "IMID2", source: PURMASTH },
    {
      header: "SUPLR\nUPC",
      code: "IGLO2",
      source: PURMASTH,
      lowConfidence: true,
    },
    { header: "ITEM\nUPC", code: "IITM2", source: PURMASTH },
    { header: "SD", code: "ISHD2", source: PURMASTH },
    { header: "SUPL#\n3", code: "ISUPL3", source: PURMASTH },
    { header: "CNTRY", code: "ISCTR3", source: PURMASTH },
    {
      header: "SUPPLIER 3\nPRODUCT #",
      code: "ISPRD3",
      source: PURMASTH,
      maxLength: 25,
      comment:
        ".Supplier 3 Prouduct #\n.Max length: 25 (AN)\n.Enter '/////////////////////////' (total 25 '/')             for blank value.\n.Leave this field blank if no uploaded data",
    },
    { header: "UOM", code: "ISUOM3", source: PURMASTH },
    {
      header: "UOM\nFACTOR\n5 IML DEC",
      code: "ISUFC3",
      source: PURMASTH,
      maxLength: 9,
    },
    { header: "MIN\nQTY", code: "ISMQT3", source: PURMASTH },
    {
      header: "MIN\nAMT $\n2 IMP DEC",
      code: "ISMAM3",
      source: PURMASTH,
      maxLength: 9,
    },
    { header: "UPC PACK 1ST Char", code: "IPAK3", source: PURMASTH },
    { header: "UPC MFTR ID 2ND Char", code: "IMID3", source: PURMASTH },
    {
      header: "SUPLR\n3 UPC",
      code: "IGLO3",
      source: PURMASTH,
      lowConfidence: true,
    },
    { header: "ITEM\nUPC", code: "IITM3", source: PURMASTH },
    { header: "SD", code: "ISHD3", source: PURMASTH },
    { header: "PR STS", code: "ISTATH", source: PURMASTH },
    { header: "Y/N", code: null, key: "UPDATE_ISTATH", maxLength: 1 },
    { header: "SUPL 1\nQUOTE ", code: "ISQTE1", source: PURMASTH },
    { header: "Y/N", code: null, key: "UPDATE_ISQTE1", maxLength: 1 },
    { header: "SUPL 2\nQUOTE ", code: "ISQTE2", source: PURMASTH },
    { header: "Y/N", code: null, key: "UPDATE_ISQTE2", maxLength: 1 },
    { header: "SUPL 3\nQUOTE ", code: "ISQTE3", source: PURMASTH },
    { header: "Y/N", code: null, key: "UPDATE_ISQTE3", maxLength: 1 },
    {
      header: "Chg Supp1 \nMinQty Y/N ",
      code: null,
      key: "CHG_ISMQT1",
      maxLength: 1,
    },
    {
      header: "Chg Supp2 \nMinQty Y/N ",
      code: null,
      key: "CHG_ISMQT2",
      maxLength: 1,
    },
    {
      header: "Chg Supp3 \nMinQty Y/N ",
      code: null,
      key: "CHG_ISMQT3",
      maxLength: 1,
    },
    {
      header: "Chg\nSupp1 \nMinAmt Y/N ",
      code: null,
      key: "CHG_ISMAM1",
      maxLength: 1,
    },
    {
      header: "Chg\nSupp2\nMinAmt Y/N ",
      code: null,
      key: "CHG_ISMAM2",
      maxLength: 1,
    },
    {
      header: "Chg\nSupp3 \nMinAmt Y/N ",
      code: null,
      key: "CHG_ISMAM3",
      maxLength: 1,
    },
    { header: "SUPL 1\nNAME ", code: "ISNAM1", source: PURMASTH },
    { header: "SUPL 2\nNAME ", code: "ISNAM2", source: PURMASTH },
    { header: "SUPL 3\nNAME", code: "ISNAM3", source: PURMASTH },
    {
      header: "PRODUCT HEIGHT-EACH          ",
      code: "IHIGEA",
      source: PURMASTD,
      maxLength: 10,
    },
    { header: "UPDATE   Y/N", code: null, key: "UPDATE_IHIGEA", maxLength: 1 },
    {
      header: "PRODUCT WIDTH -EACH          ",
      code: "IWIDEA",
      source: PURMASTD,
      maxLength: 10,
    },
    { header: "UPDATE Y/N", code: null, key: "UPDATE_IWIDEA", maxLength: 1 },
    {
      header: "PRODUCT LENGTH-EACH",
      code: "ILENEA",
      source: PURMASTD,
      maxLength: 10,
    },
    { header: "UPDATE Y/N", code: null, key: "UPDATE_ILENEA", maxLength: 1 },
    {
      header: "PRODUCT WEIGHT-EACH",
      code: "IWHTEA",
      source: PURMASTD,
      maxLength: 10,
    },
    { header: "UPDATE Y/N", code: null, key: "UPDATE_IWHTEA", maxLength: 1 },
    {
      header: "PRODUCT DENSITY-EACH",
      code: "IDNSEA",
      source: PURMASTD,
      lowConfidence: true,
      maxLength: 10,
    },
    { header: "UPDATEY/N", code: null, key: "UPDATE_IDNSEA", maxLength: 1 },
    {
      header: "PRODUCT HEIGHT-PACKAGE      ",
      code: "IHIGPG",
      source: PURMASTD,
      maxLength: 10,
    },
    { header: "UPDATE   Y/N", code: null, key: "UPDATE_IHIGPG", maxLength: 1 },
    {
      header: "PRODUCT WIDTH-PACKAGE",
      code: "IWIDPG",
      source: PURMASTD,
      maxLength: 10,
    },
    { header: "UPDATE Y/N", code: null, key: "UPDATE_IWIDPG", maxLength: 1 },
    {
      header: "PRODUCT LENGTH-PACKAGE",
      code: "ILENPG",
      source: PURMASTD,
      maxLength: 10,
    },
    { header: "UPDATE Y/N", code: null, key: "UPDATE_ILENPG", maxLength: 1 },
    {
      header: "PRODUCT WEIGHT-PACKAGE",
      code: "IWHTPG",
      source: PURMASTD,
      maxLength: 10,
    },
    { header: "UPDATE Y/N", code: null, key: "UPDATE_IWHTPG", maxLength: 1 },
    {
      header: "PRODUCT DENSITY-PACKAGE",
      code: "IDNSPG",
      source: PURMASTD,
      maxLength: 10,
    },
    { header: "UPDATEY/N", code: null, key: "UPDATE_IDNSPG", maxLength: 1 },
    {
      header: "PRODUCT HEIGHT-PALLET    ",
      code: "IHIGPT",
      source: PURMASTD,
      maxLength: 10,
    },
    { header: "UPDATE   Y/N", code: null, key: "UPDATE_IHIGPT", maxLength: 1 },
    {
      header: "PRODUCT WIDTH-PALLET",
      code: "IWIDPT",
      source: PURMASTD,
      maxLength: 10,
    },
    { header: "UPDATE Y/N", code: null, key: "UPDATE_IWIDPT", maxLength: 1 },
    {
      header: "PRODUCT LENGTH-PALLET",
      code: "ILENPT",
      source: PURMASTD,
      maxLength: 10,
    },
    { header: "UPDATE Y/N", code: null, key: "UPDATE_ILENPT", maxLength: 1 },
    {
      header: "PRODUCT WEIGHT-PALLET",
      code: "IWHTPT",
      source: PURMASTD,
      maxLength: 10,
    },
    { header: "UPDATE Y/N", code: null, key: "UPDATE_IWHTPT", maxLength: 1 },
    {
      header: "PRODUCT DENSITY-PALLET",
      code: "IDNSPT",
      source: PURMASTD,
      lowConfidence: true,
      maxLength: 10,
    },
    { header: "UPDATEY/N", code: null, key: "UPDATE_IDNSPT", maxLength: 1 },
    { header: "PROD WARN CODE 1", code: "IOWRN1", source: PURMASTD },
    { header: "UPDATE Y/N", code: null, key: "UPDATE_IOWRN1", maxLength: 1 },
    { header: "PROD WARN CODE 2", code: "IOWRN2", source: PURMASTD },
    { header: "UPDATE Y/N", code: null, key: "UPDATE_IOWRN2", maxLength: 1 },
    { header: "PROD WARN CODE 3", code: "IOWRN3", source: PURMASTD },
    { header: "UPDATE Y/N", code: null, key: "UPDATE_IOWRN3", maxLength: 1 },
    { header: "Product Remark Eng 1", code: "IOTH1E", source: PURMASTD },
    { header: "UPDATE Y/N", code: null, key: "UPDATE_IOTH1E", maxLength: 1 },
    { header: "Product Remark Eng 2", code: "IOTH2E", source: PURMASTD },
    { header: "UPDATE Y/N", code: null, key: "UPDATE_IOTH2E", maxLength: 1 },
    { header: "Product Remark Frn 1", code: "IOTH1F", source: PURMASTD },
    { header: "UPDATE Y/N", code: null, key: "UPDATE_IOTH1F", maxLength: 1 },
    { header: "Product Remark Frn 2", code: "IOTH2F", source: PURMASTD },
    { header: "UPDATE Y/N", code: null, key: "UPDATE_IOTH2F", maxLength: 1 },
  ],
  PRICE: [
    { header: "ITEMNO", code: "ITEMNO", maxLength: 20 },
    { header: "2 IMPL DEC\nPCPRC1", code: "PCPRC1", maxLength: 9 },
    {
      header: "Reset1",
      code: null,
      key: "RESET1",
      maxLength: 1,
      comment: "To Reset PCPRC1 - Select 'Y' and leave PCPRC1 blank.",
    },
    { header: "2 IMPL DEC\nPCPRC2", code: "PCPRC2", maxLength: 9 },
    {
      header: "Reset2",
      code: null,
      key: "RESET2",
      maxLength: 1,
      comment: "To Reset PCPRC2 - Select 'Y' and leave PCPRC2 blank.",
    },
    { header: "2 IMPL DEC\nPCPRC3", code: "PCPRC3", maxLength: 9 },
    {
      header: "Reset3",
      code: null,
      key: "RESET3",
      maxLength: 1,
      comment: "To Reset PCPRC3 - Select 'Y' and leave PCPRC3 blank.",
    },
    { header: "2 IMPL DEC\nPCPRC4", code: "PCPRC4", maxLength: 9 },
    {
      header: "Reset4",
      code: null,
      key: "RESET4",
      maxLength: 1,
      comment: "To Reset PCPRC4 - Select 'Y' and leave PCPRC4 blank.",
    },
    { header: "2 IMPL DEC\nPCPRC5", code: "PCPRC5", maxLength: 9 },
    {
      header: "Reset5",
      code: null,
      key: "RESET5",
      maxLength: 1,
      comment: "To Reset PCPRC5 - Select 'Y' and leave PCPRC5 blank.",
    },
    { header: "2 IMPL DEC\nPCPRC6", code: "PCPRC6", maxLength: 9 },
    {
      header: "Reset6",
      code: null,
      key: "RESET6",
      maxLength: 1,
      comment: "To Reset PCPRC6 - Select 'Y' and leave PCPRC6 blank.",
    },
    { header: "2 IMPL DEC\nPCPRC7", code: "PCPRC7", maxLength: 9 },
    {
      header: "Reset7",
      code: null,
      key: "RESET7",
      maxLength: 1,
      comment: "To Reset PCPRC7 - Select 'Y' and leave PCPRC7 blank.",
    },
    { header: "2 IMPL DEC\nPCPRC8", code: "PCPRC8", maxLength: 9 },
    {
      header: "Reset8",
      code: null,
      key: "RESET8",
      maxLength: 1,
      comment: "To Reset PCPRC8 - Select 'Y' and leave PCPRC8 blank.",
    },
    { header: "2 IMPL DEC\nPCPRC9", code: "PCPRC9", maxLength: 9 },
    {
      header: "Reset9",
      code: null,
      key: "RESET9",
      maxLength: 1,
      comment: "To Reset PCPRC9 - Select 'Y' and leave PCPRC9 blank.",
    },
  ],
  ITEM_ADD: [
    { header: "PRODUCT CODE", code: "IDITEM", maxLength: 20 },
    { header: "L\nC", code: "IDLANG" },
    { header: "L#", code: "IDLINE" },
    { header: "ADDITION DESCRIPTION", code: "IDDESC", maxLength: 40 },
    {
      header: "CHG ADDITIONAL DESCRIPTION FLAG (Y/N/D)",
      code: null,
      key: "CHG_IDDESC",
      maxLength: 1,
      comment:
        "Change Additional Description Flag (Y/N/D or Blank)\nMax length: 1AN\nValid Value: Y, N or Blank",
    },
    { header: "", code: null },
  ],
  MATRIX_CLASSIFICATION: [
    { header: "PRODUCT", code: "IMXITEM", maxLength: 20 },
    {
      header: "MATRIX CLASS",
      code: "IMXCLASS",
      maxLength: 5,
      comment: "Character 5",
    },
    {
      header: "MATRIX CLASS UPDATE FLAG",
      code: "IMXMCCHG",
      maxLength: 1,
      comment:
        "If Y then Matrix Class will be updated with value from column B",
    },
    {
      header: "SENSITIVITY SCORE",
      code: "IMXSCOR",
      maxLength: 6,
      comment: "Numeric 5 (2 decimals)",
    },
    {
      header: "SENSITIVITY SCORE UPDATE FLAG",
      code: null,
      key: "UPDATE_IMXSCOR",
      maxLength: 1,
      comment:
        "If Y Sensitivity Score will be updated with value from column D",
    },
    {
      header: "CEILING MARGIN %",
      code: "IMXMGCL",
      maxLength: 6,
      comment: "Numeric 5 (2 decimals)",
    },
    {
      header: "Reset1",
      code: null,
      key: "RESET1",
      maxLength: 1,
      comment:
        "To clear Ceiling Margin % select 'Y' and leave Ceiling Margin % field blank.",
    },
    {
      header: "TARGET MARGIN %",
      code: "IMXMGTG",
      maxLength: 6,
      comment: "Numeric 5 (2 decimals)",
    },
    {
      header: "Reset2",
      code: null,
      key: "RESET2",
      maxLength: 1,
      comment:
        "To clear Target Margin % select 'Y' and leave Target Margin % field blank.",
    },
    {
      header: "FLOOR MARGIN %",
      code: "IMXMGFL",
      maxLength: 6,
      comment: "Numeric 5 (2 decimals)",
    },
    {
      header: "Reset3",
      code: null,
      key: "RESET3",
      maxLength: 1,
      comment:
        "To clear Floor Margin % select 'Y' and leave Floor  Margin % field blank.",
    },
    {
      header: "CEILING PRICE  (LEVEL 4)",
      code: "IMXPRCL",
      maxLength: 12,
      comment: "Numeric 11 (4 decimals)",
    },
    {
      header: "Reset4",
      code: null,
      key: "RESET4",
      maxLength: 1,
      comment:
        "To clear Ceiling Price Level 4 select 'Y' and leave Ceiling Price Level 4 field blank.",
    },
    {
      header: "TARGET PRICE  (LEVEL 3)",
      code: "IMXPRTG",
      maxLength: 12,
      comment: "Numeric 11 (4 decimals)",
    },
    {
      header: "Reset5",
      code: null,
      key: "RESET5",
      maxLength: 1,
      comment:
        "To clear Target Price Level 3 select 'Y' and leave Target Price Level 3 field blank.",
    },
    {
      header: "FLOOR PRICE  (LEVEL 2)",
      code: "IMXPRFL",
      maxLength: 12,
      comment: "Numeric 11 (4 decimals)",
    },
    {
      header: "Reset6",
      code: null,
      key: "RESET6",
      maxLength: 1,
      comment:
        "To clear Floor Price Level 2 select 'Y' and leave Floor Price Level 2 field blank.",
    },
    {
      header: "CEILING DISCOUNT %",
      code: "IMXDPCL",
      maxLength: 6,
      comment: "Numeric 5 (2 decimals)",
    },
    {
      header: "Reset7",
      code: null,
      key: "RESET7",
      maxLength: 1,
      comment:
        "To clear Ceiling Discount % select 'Y' and leave Ceiling Discount % field blank.",
    },
    {
      header: "TARGET DISCOUNT %",
      code: "IMXDPTG",
      maxLength: 6,
      comment: "Numeric 5 (2 decimals)",
    },
    {
      header: "Reset8",
      code: null,
      key: "RESET8",
      maxLength: 1,
      comment:
        "To clear Target Discount % select 'Y' and leave Target Discount % field blank",
    },
    {
      header: "FLOOR DISCOUNT %",
      code: "IMXDPFL",
      maxLength: 6,
      comment: "Numeric 5 (2 decimals)",
    },
    {
      header: "Reset9",
      code: null,
      key: "RESET9",
      maxLength: 1,
      comment:
        "To clear Floor Discount % select 'Y' and leave Floor Discount % field blank",
    },
    {
      header: "CEILING DISCOUNT MULTIPLIER",
      code: "IMXDMCL",
      maxLength: 8,
      comment: "Numeric 7 (4 decimals)",
    },
    {
      header: "Reset10",
      code: null,
      key: "RESET10",
      maxLength: 1,
      comment:
        "To clear Ceiling Discount Multiplier select 'Y' and leave Ceiling Discount Multiplier field blank",
    },
    {
      header: "TARGET DISCOUNT MULTIPLIER",
      code: "IMXDMTG",
      maxLength: 8,
      comment: "Numeric 7 (4 decimals)",
    },
    {
      header: "Reset11",
      code: null,
      key: "RESET11",
      maxLength: 1,
      comment:
        "To clear Target Discount Multiplier select 'Y' and leave Target Discount Multiplier field blank",
    },
    {
      header: "FLOOR DISCOUNT MULTIPLIER",
      code: "IMXDMFL",
      maxLength: 8,
      comment: "Numeric 7 (4 decimals)",
    },
    {
      header: "Reset12",
      code: null,
      key: "RESET12",
      maxLength: 1,
      comment:
        "To clear Floor Discount Multiplier select 'Y' and leave Floor Discount Multiplier field blank",
    },
    {
      header: "UPDATE MATRIX PRICE FLAG",
      code: "IMXUPDF",
      maxLength: 1,
      comment: "Valid values Y/N",
    },
    {
      header: "ALLOW PRICE ROUNDUP FLAG",
      code: "IMXRNDF",
      maxLength: 1,
      comment: "Valid values Y/N",
    },
    {
      header: "WEB PRICE LINE",
      code: "IMXPRLN",
      maxLength: 5,
      comment: "Character 5",
    },
  ],
};

/**
 * Marks a worksheet column the AS/400 tolerates being left blank when the
 * upload isn't changing it. The workbook says so in the column's own note —
 * "Leave this field blank or zero if no change is make to this field" (CORE
 * COST), "Leave this field blank if no uploaded data" (the supplier product
 * numbers).
 *
 * The phrase is self-referential on purpose: the `Reset*` pseudo-columns tell
 * you to leave *another* field blank ("select 'Y' and leave PCPRC1 blank"), and
 * they are not optional in this sense. `As400ExportColumns.optional.test.ts`
 * pins the exact set this matches.
 */
const OPTIONAL_COLUMN_NOTE = "leave this field blank";

/** True when the column's worksheet note marks it optional (see above). */
export function isOptionalAs400Column(column: As400ExportColumn): boolean {
  if (!column.comment) return false;
  return column.comment
    .replace(/\s+/g, " ")
    .toLowerCase()
    .includes(OPTIONAL_COLUMN_NOTE);
}

/**
 * The field codes of a group's optional columns. Used to blank cells the upload
 * isn't changing when "Fill optional fields" is off.
 */
export function as400OptionalColumnCodes(group: As400ExportGroup): Set<string> {
  const codes = new Set<string>();
  for (const column of AS400_EXPORT_WORKSHEET_COLUMNS[group]) {
    if (column.code && isOptionalAs400Column(column)) codes.add(column.code);
  }
  return codes;
}

/**
 * Edit key → the column's character limit, for those of the given columns that
 * declare one ({@link As400ExportColumn.maxLength}). Keyed the way cells are
 * addressed ({@link as400ColumnEditKey}), so a caller holding a cell's key can
 * look its limit up directly. A key that is absent has no stated limit — see
 * the field's own note on why that is "unknown", not "unlimited".
 */
export function as400ColumnMaxLengths(
  columns: readonly As400ExportColumn[],
): ReadonlyMap<string, number> {
  const limits = new Map<string, number>();
  for (const column of columns) {
    const editKey = as400ColumnEditKey(column);
    if (editKey && column.maxLength !== undefined) {
      limits.set(editKey, column.maxLength);
    }
  }
  return limits;
}

/**
 * Ordered, de-duplicated AS/400 field codes a group sources data from — the
 * columns the flattened query actually SELECTs (worksheet order; the first is
 * the join/sort key). Unmapped worksheet columns are excluded.
 */
export function as400ExportColumnCodes(group: As400ExportGroup): string[] {
  const seen = new Set<string>();
  const codes: string[] = [];
  for (const { code } of AS400_EXPORT_WORKSHEET_COLUMNS[group]) {
    if (code && !seen.has(code)) {
      seen.add(code);
      codes.push(code);
    }
  }
  return codes;
}

/**
 * Field code → the ingest table-name suffix declared to own it
 * ({@link As400ExportColumn.source}). Codes without a declaration (all of a
 * single-file-type group's) are absent — the group's one file type owns them.
 */
export function as400ExportColumnSources(
  group: As400ExportGroup,
): ReadonlyMap<string, string> {
  const bySuffix = new Map<string, string>();
  for (const { code, source } of AS400_EXPORT_WORKSHEET_COLUMNS[group]) {
    if (code && source) bySuffix.set(code, source);
  }
  return bySuffix;
}

/**
 * The column each AS/400 view is searched by — its item number, which is the
 * SKU the AS/400 keys the record on. The review page's search box is a
 * shorthand for a `contains` filter on this column, so the term is evaluated by
 * the ingest server (a predicate on the mirror) rather than app-side over a
 * fetched page.
 *
 * It is `ITEMNO` in most views; the exceptions are tables that name the item
 * column differently (the Item Add and Matrix Classification worksheets, and
 * the GAINS cost / associated-items / quantities views).
 */
export const AS400_EXPORT_GROUP_SEARCH_COLUMN: Record<
  As400ExportGroup,
  string
> = {
  ITEM: "ITEMNO",
  PURCHASE_DATA: "ITEMNO",
  PRICE: "ITEMNO",
  ITEM_ADD: "IDITEM",
  MATRIX_CLASSIFICATION: "IMXITEM",
};

export const AS400_VIEW_GROUP_SEARCH_COLUMN: Record<As400ViewGroup, string> = {
  ITEMMAST5: "ITEMNO",
  ADDITIONAL_DESCRIPTION: "ITEMNO",
  GAINS_COST: "PRODUCT",
  ASSOCIATED_ITEMS: "ASPROD",
  QUANTITIES: "ITEM NO",
};

// No total count here: the exact COUNT(*) can be far slower than the page
// query (trigram search, FDW metadata filters), so it must not block first
// draw. Clients fetch it separately via `countCatalog` and render "of many"
// until it resolves; `hasNextPage` (from a pageSize+1 fetch) keeps prev/next
// paging working in the meantime.
export const ListItemCatalogResponseSchema = z.object({
  edges: z.array(ItemCatalogItemSchema),
  pageInfo: z.object({
    currentPage: z.number(),
    hasNextPage: z.boolean(),
    hasPreviousPage: z.boolean(),
    /**
     * Present on the ingest-scan path: the token that opens the next page (null
     * once the scan is exhausted). Absent when the page came from the plain
     * product query, which is addressable by number.
     */
    nextCursor: z.string().nullable().optional(),
  }),
});

// Exact count of the catalog rows matching the same filters as `listCatalog`
// (page/pageSize and sortDirection don't affect the count and are omitted).
export const CountItemCatalogInputSchema = ItemCatalogFilterSchema.omit({
  sortDirection: true,
})
  .extend({
    // Advanced filters on ProductV3's own columns only. Filters on raw ingest
    // columns are deliberately not accepted: those are resolved by walking the
    // ingest tables (see `cursor`), and counting every product they match would
    // mean walking all of them — so a filtered catalog pages without a total
    // and the client simply doesn't ask for one.
    advancedFilters: z
      .array(CatalogBaseAdvancedFilterSchema)
      .max(20)
      .optional(),
    // Same change-set scope as listCatalog.
    changeSetId: z.string().uuid().optional(),
    // Same item-list scope as listCatalog.
    itemListId: z.string().uuid().optional(),
  })
  .superRefine(refineCatalogSearch);

export const CountItemCatalogResponseSchema = z.object({
  totalCount: z.number().int(),
});

// ─── Item-number resolution ──────────────────────────────────────────────────
// Translates raw item numbers (AS/400 ITEMNO, shown as `displaySku`) into the
// org's products. Matching is on trimmed, uppercased `displaySku`;
// `displaySku` is nullable and NOT unique, so one input can match several
// products (multiple keys/divisions) — every match is returned, one entry per
// (input, product) pair, and inputs that match nothing come back in
// `unmatchedInputs` so no loss is silent.
export const ResolveItemNumbersInputSchema = z.object({
  itemNumbers: z.array(z.string().trim().min(1)).min(1).max(50000),
});

export const ResolveItemNumbersResponseSchema = z.object({
  matched: z.array(
    z.object({
      // The raw input that matched — duplicate spellings are folded onto the
      // first one seen.
      input: z.string(),
      // ProductV3.key — the product's explicit string primary key.
      productKey: z.string(),
      displaySku: z.string(),
    }),
  ),
  unmatchedInputs: z.array(z.string()),
});

export const ListRexelItemsInputSchema = z.object({
  baseSku: z.string(),
});

export const InitinfcRecordSchema = z.object({
  id: z.string().uuid(),
  division: z.string(),
  fileVariant: z.string(),
  giitem: z.string(),
  giloc: z.string(),
  gidept: z.string().nullable(),
  gigroup: z.string().nullable(),
  gidstlvl: z.string().nullable(),
  gidescr: z.string().nullable(),
  giselprc: z.string().nullable(),
  gistdcst: z.string().nullable(),
  gifobcst: z.string().nullable(),
  giwght: z.string().nullable(),
  gicnvfct: z.string().nullable(),
  gimaxqty: z.string().nullable(),
  giminqty: z.string().nullable(),
  giincord: z.string().nullable(),
  gilead: z.string().nullable(),
  gisrvlvl: z.string().nullable(),
  gishlife: z.string().nullable(),
  gircvcst: z.string().nullable(),
  gicrycst: z.string().nullable(),
  giprcbrk: z.string().nullable(),
  gifrtbrk: z.string().nullable(),
  gimodel: z.string().nullable(),
  giinvcls: z.string().nullable(),
  gistkflg: z.string().nullable(),
  gionhand: z.number().int(),
  giunshpq: z.number().int(),
  gistrdat: z.string().nullable(),
  giparent1: z.string().nullable(),
  gipercent1: z.string().nullable(),
  giparent2: z.string().nullable(),
  gipercent2: z.string().nullable(),
  gimaxtyp: z.string().nullable(),
  gimaxval: z.string().nullable(),
  gissmin: z.string().nullable(),
  giuom: z.string().nullable(),
  gitype: z.string().nullable(),
  girsq: z.string().nullable(),
  girsqapl: z.string().nullable(),
  gilifetyp: z.string().nullable(),
  gibuyer: z.string().nullable(),
  gichar1: z.string().nullable(),
  gichar2: z.string().nullable(),
  gichar3: z.string().nullable(),
  gichar4: z.string().nullable(),
  ginum1: z.string().nullable(),
  ginum2: z.string().nullable(),
  ginum3: z.string().nullable(),
  ginum4: z.string().nullable(),
  gieor: z.string().nullable(),
});

export const ListRexelItemsResponseSchema = z.object({
  edges: z.array(InitinfcRecordSchema),
});

// ─── Contract ───────────────────────────────────────────────────────────────

export const ListPriceListLinesInputSchema = z.object({
  productId: z.string().uuid(),
});

export const PriceListLineItemSchema = z.object({
  id: z.string().uuid(),
  priceListId: z.string().uuid().nullable(),
  priceListName: z.string().nullable(),
  unitPrice: z.string(),
  uomCode: z.string().nullable(),
  pricingMethod: z.string().nullable(),
  effectiveFrom: z.string().nullable(),
  effectiveTo: z.string().nullable(),
});

export const ListPriceListLinesResponseSchema = z.object({
  edges: z.array(PriceListLineItemSchema),
});

export const ListProductRawDataInputSchema = z.object({
  // ProductV3.key — the product's explicit string primary key.
  productKey: z.string(),
  /**
   * The catalog's active advanced filters. When any of them targets a raw
   * metadata column, the linked records are narrowed to the ones that actually
   * satisfy the filter set: only ingest tables carrying *every* filtered column
   * are read, and only rows meeting *all* of the filters (AND) are returned —
   * so the expanded row shows the records that matched, not every record the
   * product happens to link. Base ProductV3 filters are ignored here (they
   * constrain which products are listed, not which of their records match).
   */
  advancedFilters: z.array(CatalogAdvancedFilterSchema).max(20).optional(),
});

// A change set's staged field edits (field name → new value). Present only on
// change-set-scoped reads; null when nothing is staged for the record/row.
const StagedValuesSchema = z
  .record(z.string(), z.unknown())
  .nullable()
  .optional();

// One linked raw ingest row for a product. `row` is the raw record's columns
// (arbitrary per ingest table); null when the row can't be read (table dropped
// or ingest DB unavailable). `fieldDescriptions` maps each raw column name
// (upper-cased) to the description of the SchemaFieldDefinition that produced
// it, for surfacing as column-header tooltips — empty when the linked schema's
// fields carry no descriptions.
export const ProductRawDataRecordSchema = z.object({
  productKey: z.string(),
  // The im_product_v3_data_links row this record was resolved through.
  dataLinkId: z.string().uuid(),
  dataSourceLinkId: z.string().uuid(),
  dataSourceName: z.string().nullable(),
  dataSourceGroupName: z.string().nullable(),
  // Identity of the shared schema definition (`core_schema_definitions`) this
  // link conforms to. Many data sources map their own physical foreign table
  // (e.g. `qe_as_item`, `qo_as_item`) onto the same schema definition
  // (`as_item`); the client groups same-definition rows into one table by id.
  schemaDefinitionId: z.string().uuid(),
  schemaDefinitionName: z.string(),
  schemaName: z.string(),
  tableName: z.string(),
  dataId: z.string().uuid(),
  row: z.record(z.string(), z.unknown()).nullable(),
  fieldDescriptions: z.record(z.string(), z.string().nullable()),
});

export const ListProductRawDataResponseSchema = z.object({
  edges: z.array(ProductRawDataRecordSchema),
});

// A distinct raw metadata column available for advanced filtering, sourced
// from the schema field definitions of the org's linked ingest tables — an
// app-DB-only lookup, no ingest rows are read.
export const CatalogMetadataColumnSchema = z.object({
  name: z.string(),
  description: z.string().nullable(),
});

export const ListCatalogMetadataColumnsResponseSchema = z.object({
  columns: z.array(CatalogMetadataColumnSchema),
});

// ─── AS/400 export flattened view ────────────────────────────────────────────
// A paginated, flattened view of the ingest metadata records behind one AS/400
// export file format, restricted to one change set's products (the review
// detail page — the only place these worksheets are shown). Paging runs over
// the change set's membership table (exact totals); the page's cells come from
// the FDW mirrors of the matched ingest tables (the selected DataSource's links
// whose table name ends in the group's suffix, e.g. `_as_price`), read by
// surrogate id. The queried columns are `as400ExportColumnCodes(group)`.
// PURCHASE_DATA joins its two file types (purmasth + purmastd) on `itemno`.
export const ListAs400ExportInputSchema = z.object({
  group: As400ExportGroupSchema,
  // The divisions to read from: one or more DataSources. Only their links'
  // ingest tables (matched by the group's suffix) are queried. Passing several
  // combines them into one flattened view (each division's rows concatenated),
  // so the on-screen table can show the divisions together.
  dataSourceIds: z.array(z.string().uuid()).min(1).max(50),
  // The page number: the view pages the change set's local membership table.
  page: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(200).default(50),
  // Advanced filters on the view's own columns, evaluated by the ingest server
  // (a pushed-down predicate on the mirror, not an app-side pass over fetched
  // rows). A product is kept when one of its ingest rows in this view satisfies
  // every filter. Filters referencing a column not in the group match nothing.
  advancedFilters: z.array(CatalogAdvancedFilterSchema).max(20).optional(),
  // The change set whose products the view covers. Rows carry `stagedValues`
  // and the pageInfo carries exact totals.
  changeSetId: z.string().uuid(),
});

// One ingest source row behind an export row: which data-source link's table it
// came from and the surrogate `id` of the row there. A single-table export row
// has one source; a joined view (Purchase Data) has up to two.
export const As400ExportRowSourceSchema = z.object({
  dataSourceLinkId: z.string().uuid(),
  dataId: z.string().uuid(),
});

export const As400ExportRowSchema = z.object({
  // Stable identity for row selection: the row's source (linkId,dataId) pairs
  // joined. Unique per row across pages so selection survives paging.
  key: z.string(),
  // The ingest source rows this export row is built from (1, or 2 when joined).
  sources: z.array(As400ExportRowSourceSchema),
  // Displayed cell values keyed by canonical field code; text or null.
  values: z.record(z.string(), z.string().nullable()),
  // Staged field edits (field code → new value) from the requested change set,
  // merged across the row's sources.
  stagedValues: StagedValuesSchema,
});

// A net-new worksheet row authored on a change set: a product the AS/400
// doesn't have yet, staged so the upload file can create it. `values` is
// keyed by the worksheet column's edit key (`as400ColumnEditKey`).
export const ChangeSetNewRowSchema = z.object({
  id: z.string().uuid(),
  group: As400ExportGroupSchema,
  // The division (DataSource) whose upload file carries the row.
  dataSourceId: z.string().uuid(),
  values: z.record(z.string(), z.string().nullable()),
  createdAt: z.string(),
});
export type ChangeSetNewRow = z.infer<typeof ChangeSetNewRowSchema>;

export const ListAs400ExportResponseSchema = z.object({
  // Ordered canonical field codes present in each row (matches
  // as400ExportColumnCodes(group)). Row keys; not the display headers — the
  // client renders worksheet headers from AS400_EXPORT_WORKSHEET_COLUMNS.
  columns: z.array(z.string()),
  // Each row: selection identity + its ingest sources + the value record.
  rows: z.array(As400ExportRowSchema),
  // The change set's new rows for this worksheet + division scope, pinned
  // ahead of the ingest-backed rows. Returned on page 1 with no advanced
  // filters active — filters are evaluated by the ingest server, which these
  // rows never reached, so rather than half-applying a predicate they hide
  // behind `hiddenNewRowCount` (change-set scope only).
  newRows: z.array(ChangeSetNewRowSchema).optional(),
  hiddenNewRowCount: z.number().int().optional(),
  // No total count in the unscoped view: an exact count(*) over the
  // multi-million-row FDW union is prohibitively slow, so paging is
  // cursor-style (prev/next via hasNextPage). The change-set-scoped view pages
  // over a local membership table, so it carries exact totals.
  pageInfo: z.object({
    currentPage: z.number(),
    hasNextPage: z.boolean(),
    hasPreviousPage: z.boolean(),
    totalCount: z.number().int().optional(),
    totalPages: z.number().int().optional(),
    /**
     * False when `totalCount` is a floor rather than the true total — the
     * change set was too large to sweep in one request. The pager then falls
     * back to next/previous.
     */
    totalCountExact: z.boolean().optional(),
    /** Cursor path only (the item catalog's AS/400 view) — see `cursor`. */
    nextCursor: z.string().nullable().optional(),
  }),
});

// ─── AS/400 view flattened over the catalog filter's data links ──────────────
// The "AS/400 View" grid: the flattened raw data links of the products matching
// the catalog filter (free-text search + advanced filters, and the change set
// when one is in view), narrowed to one column group (ITEMMAST5,
// ADDITIONAL_DESCRIPTION, GAINS_COST, ASSOCIATED_ITEMS, QUANTITIES). One row
// per (product × linked raw record); columns are the group's fields. Paging is
// by product — on the Item Catalog's Search tab in lockstep with the Gently
// list (exact total from `countCatalog`); on the review page's Queries views
// on its own. Rows carry `stagedValues` and are editable in change-set scope;
// the item-catalog view is read-only.
//
// `advancedFilters` scope the *products* (a product is kept when one of its
// linked ingest rows satisfies every filter), evaluated by the ingest server as
// a pushed-down predicate on the mirrors. The review page's search box is one
// such filter, on AS400_VIEW_GROUP_SEARCH_COLUMN[group]; `search` is the
// catalog's own SKU/description text search, which only the Search tab uses.
export const ListCatalogAs400ViewInputSchema = ItemCatalogFilterSchema.extend({
  group: As400ViewGroupSchema,
  // Restrict the rows to links belonging to these divisions (DataSources) —
  // the review page's division selector. Omitted = every linked source, each
  // row naming its own in the Division column.
  dataSourceIds: z.array(z.string().uuid()).max(50).optional(),
  ...catalogPageFields,
}).superRefine(refineCatalogSearch);

// ─── Streaming exports ───────────────────────────────────────────────────────
// The catalog page's "Export as…" downloads. Both exports are unbounded — they
// cover the whole filtered view, not a page — so the procedures resolve to a
// byte stream rather than a materialized value: oRPC pipes a `ReadableStream`
// output straight to the HTTP response, so the backend keeps at most a batch of
// rows (or, for a multi-division archive, one division's file) in memory and
// the browser writes the download to disk as it arrives.

export const ExportFileFormatSchema = z.enum(["csv", "xlsx"]);
export type ExportFileFormat = z.infer<typeof ExportFileFormatSchema>;

/**
 * The download's bytes. Not a JSON value: the handler returns the serialized
 * (and, for CSV, gzipped) file stream and sets the `Content-Type` /
 * `Content-Disposition` headers itself. Clients trigger these procedures as a
 * browser navigation to the RPC GET URL so the download streams to disk with
 * native progress, rather than calling them through the typed client.
 */
export const ExportFileStreamSchema = z.custom<ReadableStream<Uint8Array>>(
  (value) => value instanceof ReadableStream,
  { message: "Expected a ReadableStream of file bytes" },
);

// Mirrors the Search tab's on-screen state: the catalog filter plus the AS/400
// column narrowing. `as400Groups` (multi-select "AS/400 View" groups) and
// `as400ExportGroup` (single-select export file format) are mutually exclusive
// in the UI; if both arrive the export format wins.
export const ExportItemCatalogInputSchema = z
  .object({
    search: z.string().trim().optional(),
    searchMode: z.enum(["text", "regex"]).default("text"),
    isSellable: z.boolean().optional(),
    isActive: z.boolean().optional(),
    as400Groups: z.array(As400ViewGroupSchema).optional(),
    as400ExportGroup: As400ExportGroupSchema.optional(),
    // Same advanced filters and divisions the on-screen view applies, so the
    // download covers exactly the rows the screen shows.
    advancedFilters: z.array(CatalogAdvancedFilterSchema).max(20).optional(),
    dataSourceIds: z.array(z.string().uuid()).max(50).optional(),
    // Restrict to one change set's products (the review page's Queries views).
    changeSetId: z.string().uuid().optional(),
    format: ExportFileFormatSchema.default("csv"),
  })
  .superRefine(refineCatalogSearch);

// The review page's flattened AS/400 worksheet for one export file format,
// scoped to the selected division(s) and change set. `advancedFilters` are
// pushed down to the ingest mirrors, so the download covers exactly the rows
// the on-screen view shows.
export const ExportAs400ViewInputSchema = z.object({
  group: As400ExportGroupSchema,
  // The export is one file per division and per mode — the AS/400 upload runs
  // one mode per file, so a division's authored new rows (adds) and its edits to
  // existing records (changes) are always separate files. Anything but a lone
  // file downloads as a zip.
  dataSourceIds: z.array(z.string().uuid()).min(1).max(50),
  advancedFilters: z.array(CatalogAdvancedFilterSchema).max(20).optional(),
  changeSetId: z.string().uuid(),
  format: ExportFileFormatSchema.default("csv"),
  /**
   * When false, a column the workbook marks optional
   * ({@link isOptionalAs400Column}) is emitted blank unless the change set
   * actually gives it a new value — so the upload carries only real changes.
   * Defaults to true, which fills every column with its effective value.
   */
  fillOptionalFields: z.boolean().default(true),
});

/**
 * The same view as {@link ExportAs400ViewInputSchema}, asked whether it would
 * produce a file with anything in it. No `format` — the answer doesn't depend on
 * one — and no `fillOptionalFields`: that rule blanks cells of a changed row, it
 * never decides whether a row is changed.
 */
export const CountAs400ExportRowsInputSchema = ExportAs400ViewInputSchema.omit({
  format: true,
  fillOptionalFields: true,
});

export const As400ExportHasRowsResponseSchema = z.object({
  /**
   * True when at least one planned file has a row: an existing record the
   * change set changes, or an authored new row. False means there is nothing to
   * upload, so the page says so instead of downloading a header-only file.
   */
  hasRows: z.boolean(),
});

export const productsContracts = {
  listCatalog: oc
    .input(ListItemCatalogInputSchema)
    .output(ListItemCatalogResponseSchema)
    .route({ method: "GET" }),
  countCatalog: oc
    .input(CountItemCatalogInputSchema)
    .output(CountItemCatalogResponseSchema)
    .route({
      method: "GET",
      summary: "Exact count of catalog rows matching the listCatalog filters",
    }),
  resolveItemNumbers: oc
    .input(ResolveItemNumbersInputSchema)
    .output(ResolveItemNumbersResponseSchema)
    .route({
      method: "POST",
      summary: "Resolve raw item numbers to catalog products by display SKU",
    }),
  listAs400Export: oc
    .input(ListAs400ExportInputSchema)
    .output(ListAs400ExportResponseSchema)
    .route({ method: "GET" }),
  listCatalogAs400View: oc
    .input(ListCatalogAs400ViewInputSchema)
    .output(ListAs400ExportResponseSchema)
    .route({
      method: "GET",
      summary:
        "Flattened raw data links of the catalog Search filter's products, by AS/400 View group",
    }),
  listRexelItems: oc
    .input(ListRexelItemsInputSchema)
    .output(ListRexelItemsResponseSchema)
    .route({ method: "GET" }),
  listPriceListLines: oc
    .input(ListPriceListLinesInputSchema)
    .output(ListPriceListLinesResponseSchema)
    .route({ method: "GET" }),
  listRawDataRecords: oc
    .input(ListProductRawDataInputSchema)
    .output(ListProductRawDataResponseSchema)
    .route({ method: "GET" }),
  listMetadataColumns: oc
    .input(z.object({}))
    .output(ListCatalogMetadataColumnsResponseSchema)
    .route({
      method: "GET",
      summary: "Distinct raw metadata columns available as advanced filters",
    }),
  exportCatalog: oc
    .input(ExportItemCatalogInputSchema)
    .output(ExportFileStreamSchema)
    .route({
      method: "GET",
      summary:
        "Stream the whole filtered item catalog as a CSV or XLSX download",
    }),
  exportAs400View: oc
    .input(ExportAs400ViewInputSchema)
    .output(ExportFileStreamSchema)
    .route({
      method: "GET",
      summary:
        "Stream the flattened AS/400 export view as a CSV/XLSX download, or a zip of its per-division adds/changes files",
    }),
  as400ExportHasRows: oc
    .input(CountAs400ExportRowsInputSchema)
    .output(As400ExportHasRowsResponseSchema)
    .route({
      method: "GET",
      summary:
        "Whether an AS/400 export would carry any rows — asked before starting the download, which cannot report back once it is a navigation",
    }),
} as const;

// ─── Type exports ───────────────────────────────────────────────────────────

export type ItemCatalogItem = z.infer<typeof ItemCatalogItemSchema>;
export type ItemCatalogFilter = z.infer<typeof ItemCatalogFilterSchema>;
export type ListItemCatalogInput = z.infer<typeof ListItemCatalogInputSchema>;
export type ListItemCatalogResponse = z.infer<
  typeof ListItemCatalogResponseSchema
>;
export type CountItemCatalogInput = z.infer<typeof CountItemCatalogInputSchema>;
export type CountItemCatalogResponse = z.infer<
  typeof CountItemCatalogResponseSchema
>;
export type ResolveItemNumbersInput = z.infer<
  typeof ResolveItemNumbersInputSchema
>;
export type ResolveItemNumbersResponse = z.infer<
  typeof ResolveItemNumbersResponseSchema
>;
export type ListAs400ExportInput = z.infer<typeof ListAs400ExportInputSchema>;
export type ListCatalogAs400ViewInput = z.infer<
  typeof ListCatalogAs400ViewInputSchema
>;
export type ListAs400ExportResponse = z.infer<
  typeof ListAs400ExportResponseSchema
>;
export type As400ExportRow = z.infer<typeof As400ExportRowSchema>;
export type As400ExportRowSource = z.infer<typeof As400ExportRowSourceSchema>;
export type InitinfcRecord = z.infer<typeof InitinfcRecordSchema>;
export type ListRexelItemsInput = z.infer<typeof ListRexelItemsInputSchema>;
export type ListRexelItemsResponse = z.infer<
  typeof ListRexelItemsResponseSchema
>;
export type PriceListLineItem = z.infer<typeof PriceListLineItemSchema>;
export type ListPriceListLinesInput = z.infer<
  typeof ListPriceListLinesInputSchema
>;
export type ListPriceListLinesResponse = z.infer<
  typeof ListPriceListLinesResponseSchema
>;
export type ProductRawDataRecord = z.infer<typeof ProductRawDataRecordSchema>;
export type ListProductRawDataInput = z.infer<
  typeof ListProductRawDataInputSchema
>;
export type ListProductRawDataResponse = z.infer<
  typeof ListProductRawDataResponseSchema
>;
export type CatalogMetadataColumn = z.infer<typeof CatalogMetadataColumnSchema>;
export type ListCatalogMetadataColumnsResponse = z.infer<
  typeof ListCatalogMetadataColumnsResponseSchema
>;
export type ExportItemCatalogInput = z.input<
  typeof ExportItemCatalogInputSchema
>;
export type ExportAs400ViewInput = z.input<typeof ExportAs400ViewInputSchema>;
export type CountAs400ExportRowsInput = z.input<
  typeof CountAs400ExportRowsInputSchema
>;
export type As400ExportHasRowsResponse = z.infer<
  typeof As400ExportHasRowsResponseSchema
>;
